import { Consumer, Subscription } from '@rails/actioncable';
import { Principal_Read_Nested } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  type MessagingTyper,
  principalFirstName,
  principalId,
} from '~hooks/useMessaging/models';
import { realTimeChannels } from '~services/realTimeChannels';
import { Nullable } from '~types/Nullable';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';

const MESSAGING_TYPING_HANDLER_KEY = 'messaging-typing';

export const TYPING_PING_INTERVAL_IN_MS = 3000;

// Outlasts the ping interval, so steady typing never flickers.
export const TYPING_SHOWN_FOR_IN_MS = 5000;

type MessagingTypingEvent = {
  action: 'typing';
  data: { messaging_channel_id: string; principal: Principal_Read_Nested };
};

/** The envelope is absent from the OpenAPI spec by design, so it is narrowed by hand. */
const asTypingEvent = (payload: unknown): MessagingTypingEvent | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const { data, action } = payload as Partial<MessagingTypingEvent>;
  if (action !== 'typing') return undefined;
  if (
    !data ||
    typeof data.messaging_channel_id !== 'string' ||
    !data.principal ||
    typeof data.principal !== 'object'
  ) {
    return undefined;
  }

  return payload as MessagingTypingEvent;
};

export default function useMessagingChannelTypers({
  channelId,
  viewerUserId,
}: {
  channelId: Nullable<string> | undefined;
  viewerUserId: Nullable<string> | undefined;
}) {
  const handlerKey = `${MESSAGING_TYPING_HANDLER_KEY}-${useId()}`;
  const [typers, setTypers] = useState<MessagingTyper[]>([]);
  const subscriptionRef = useRef<Subscription<Consumer> | undefined>(undefined);
  const expiryTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const viewerUserIdRef = useRef(viewerUserId);
  viewerUserIdRef.current = viewerUserId;

  const dropTyper = useCallback((typerId: string) => {
    clearTimeout(expiryTimersRef.current.get(typerId));
    expiryTimersRef.current.delete(typerId);
    setTypers((current) =>
      current.some((typer) => typer.id === typerId)
        ? current.filter((typer) => typer.id !== typerId)
        : current,
    );
  }, []);

  useEffect(() => {
    if (!channelId) return;

    const expiryTimers = expiryTimersRef.current;
    const createOptions = { messaging_channel_id: channelId };

    const onTyping = (payload: unknown) => {
      const typingEvent = asTypingEvent(payload);
      if (!typingEvent || typingEvent.data.messaging_channel_id !== channelId) return;

      const typerId = principalId(typingEvent.data.principal);
      const isViewerTyping = typerId === viewerUserIdRef.current;
      if (!typerId || isViewerTyping) return;

      const name =
        principalFirstName(typingEvent.data.principal) ?? t('messaging.unknown_sender');

      clearTimeout(expiryTimers.get(typerId));
      expiryTimers.set(
        typerId,
        setTimeout(() => dropTyper(typerId), TYPING_SHOWN_FOR_IN_MS),
      );
      setTypers((current) =>
        current.some((typer) => typer.id === typerId)
          ? current
          : [...current, { id: typerId, name }],
      );
    };

    subscriptionRef.current = subscribeWithMultipleHandlers(
      realTimeChannels.MessagingTypingChannel,
      null,
      handlerKey,
      onTyping,
      createOptions,
    );

    return () => {
      unsubscribeHandler(
        realTimeChannels.MessagingTypingChannel,
        null,
        handlerKey,
        createOptions,
      );
      subscriptionRef.current = undefined;
      expiryTimers.forEach((timer) => clearTimeout(timer));
      expiryTimers.clear();
      setTypers((current) => (current.length > 0 ? [] : current));
    };
  }, [channelId, dropTyper, handlerKey]);

  const sendTyping = useCallback(
    () => subscriptionRef.current?.perform('typing') ?? false,
    [],
  );

  return { dropTyper, sendTyping, typers };
}
